"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useEditMode } from "@/contexts/EditModeContext";
import { Check, X } from "lucide-react";

export default function EditOverlay() {
  const { isEditing, editTarget, cancelEdit, updateField } = useEditMode();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editTarget) {
      setDraft(editTarget.value);
      setError(null);
    }
  }, [editTarget]);

  useEffect(() => {
    if (editTarget && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editTarget]);

  const save = useCallback(async () => {
    if (!editTarget) return;
    if (draft === editTarget.value) {
      cancelEdit();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateField(editTarget.section, editTarget.field, draft);
      cancelEdit();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [editTarget, draft, updateField, cancelEdit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        save();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        cancelEdit();
      }
    },
    [save, cancelEdit]
  );

  if (!isEditing || !editTarget) return null;

  const { section, field, value } = editTarget;

  return (
    <div
      className="bg-foreground/40 fixed inset-0 z-[200] flex items-center justify-center backdrop-blur-sm"
      role="button" tabIndex={0} onKeyDown={(e)=>{if(e.key==="Enter"||e.key===" "){e.preventDefault(); (e.currentTarget as HTMLElement).click()}}} onClick={cancelEdit}
    >
      <div
        className="bg-background border-foreground mx-4 w-full max-w-lg border-2 shadow-[6px_6px_0px_0px_var(--foreground)]"
        role="button" tabIndex={0} onKeyDown={(e)=>{if(e.key==="Enter"||e.key===" "){e.preventDefault(); (e.currentTarget as HTMLElement).click()}}} onClick={(e) => e.stopPropagation()}
      >
        <div className="border-foreground/10 flex items-center justify-between border-b px-4 py-3">
          <div>
            <p className="text-foreground text-[10px] font-bold tracking-[0.2em] uppercase">
              Editing: {section}
            </p>
            <p className="text-muted-foreground font-mono text-[10px] tracking-wider">{field}</p>
          </div>
          <button
            onClick={cancelEdit}
            className="text-foreground/50 hover:text-foreground p-1 transition-colors"
            type="button"
            aria-label="Close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <div className="border-foreground/20 bg-background border">
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              className="text-foreground placeholder:text-muted-foreground/40 w-full border-none bg-transparent px-3 py-3 font-mono text-sm tracking-wide outline-none"
              placeholder={value}
            />
          </div>

          {error && (
            <p className="border-destructive/30 bg-destructive/5 text-destructive border-l-2 px-2 py-1 text-[10px] font-bold tracking-widest uppercase">
              {error}
            </p>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-muted-foreground font-mono text-[10px]">
              Press Enter to save · Esc to cancel
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={cancelEdit}
                disabled={saving}
                className="border-foreground/20 text-foreground/60 hover:text-foreground hover:border-foreground/40 flex h-8 items-center gap-1.5 border px-3 text-[10px] font-bold tracking-[0.2em] uppercase transition-colors disabled:opacity-40"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || draft === value}
                className="bg-foreground text-background hover:bg-foreground/90 flex h-8 items-center gap-1.5 px-4 text-[10px] font-bold tracking-[0.2em] uppercase transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                type="button"
              >
                {saving ? (
                  <span className="border-background/30 border-t-background inline-block h-3 w-3 animate-spin rounded-full border-2" />
                ) : (
                  <Check size={12} strokeWidth={2.5} />
                )}
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
